<template>
  <base-modal :size="'xl'" :modal-id="'manage-column-modal'" :is-modal-open="isOpen" hide-footer @open-modal="onOpenModal" @hide="onCloseModal">
    <template #modal-header>
      <div class="manage-column-header-wrp">
        <div class="manage-column-header"><span>Manage Column</span></div>
        <div class="manage-column-header-description"><span>Select the column you'd like to see on this list</span></div>
      </div>
    </template>
    <template #modal-body>
      <div class="manage-column-content-wrp">
        <div class="content">
          <div class="left-panel">
            <div>
              <div class="table-header-column-container">
                <div class="table-header-column">
                  <div class="icon-table-column"><CustomerIcon /></div>
                  <div class="text-table-column">
                    <span>Customer</span>
                  </div>
                </div>
              </div>
              <div style="margin-top: 1rem; border-bottom: 1px solid #dee2e6" />
              <div class="column-list">
                <div class="column-item">
                  <input type="checkbox" :id="'allColumn'" v-model="isSelectAllColumn" />
                  <label>{{ 'All Column' }} </label>
                  <div class="tooltip-question">
                    <CommentBox class="title-question-icon" />
                    <span class="tooltip-text">
                      <div class="title">{{ 'All Column' }}</div>
                    </span>
                  </div>
                </div>
                <div v-for="(column, index) in columns" :key="index" class="column-item">
                  <input type="checkbox" :id="column.key" v-model="column.selected" />
                  <label :for="column.key">{{ column.label }} </label>
                  <div class="tooltip-question">
                    <CommentBox class="title-question-icon" />
                    <span class="tooltip-text">
                      <div class="title">{{ column.label }}</div>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="right-panel">
            <div style="display: flex; margin-top: 1rem; flex-direction: column; align-items: center">
              <div class="selected-column-header">
                <span>Selected Columns</span>
                <span>({{ selectedColumns.length }})</span>
              </div>
              <div :id="idColumnList" style="width: 100%">
                <ul class="selected-column-list">
                  <li
                    v-for="column in filteredColumns"
                    :id="'drop-area' + column.key"
                    :key="column.key"
                    :draggable="true"
                    @dragstart="onDragStart($event, column, column.key)"
                    @dragover="onDragOver($event, column.key)"
                    @dragend="onDragEnd($event)"
                    @drop="onDrop($event, column.key)"
                  >
                    <div style="cursor: move"><DragableIcon /></div>
                    <div class="text-selected-column-list">{{ column.label }}</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="actions">
          <button @click="onClickCancelButton" class="btn btn-outline-secondary d-flex align-items-center justify-content-center cancel-button">
            ปิด
          </button>
          <button @click="onClickApplyButton" class="btn btn-primary d-flex align-items-center justify-content-center apply-button">นำไปใช้</button>
        </div>
      </div>
    </template>
  </base-modal>
</template>
<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModal from '@/views/components/modal/BaseModal.vue';
import DragableIcon from '@/assets/icons/base/table/DragableIcon.vue';
import CommentBox from '@/assets/icons/modules/segment/all-segment/CommentBox.vue';
import CustomerIcon from '@/assets/icons/modules/analytics/churn-rate/CustomerIcon.vue';

interface Props {
  isOpen: boolean;
  loading: boolean;
  columnValue: any;
}
interface Column {
  key: string;
  label: string;
  selected: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
});
defineEmits(['close-modal', 'apply-modal']);

const { t } = useI18n();

const vm = getCurrentInstance()?.proxy;

const isSelectAllColumn = ref<boolean>(false);
const dataDragColumn = ref<Column | null>(null);
const keyDragColumn = ref<string | null>(null);
const idColumnList = ref<number>(0);
const keyDragHover = ref<string | null>(null);

const columns = ref<Column[]>([]);
const selectedColumns = ref<Column[]>([]);
const filteredColumns = computed(() => columns.value.filter((column) => column.selected));
function onDragStart(event: DragEvent, column: Column | null, key: string) {
  keyDragColumn.value = null;
  dataDragColumn.value = null;
  keyDragColumn.value = key;
  dataDragColumn.value = column;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
  }
}
let previousColumnOver: HTMLElement | null = null;

function onDragOver(event: DragEvent, key: string) {
  event.preventDefault();
  keyDragHover.value = null;
  keyDragHover.value = key;
  const indexDrag = columns.value.findIndex((item) => item.key == keyDragColumn.value);
  const indexDrop = columns.value.findIndex((item) => item.key == key);
  const thisColumnOver = document.getElementById('drop-area' + key);
  if (thisColumnOver) {
    if (previousColumnOver && previousColumnOver !== thisColumnOver) {
      previousColumnOver.classList.remove('flip-list-move');
      previousColumnOver.classList.remove('flip-list-move-top');
    }
    if (indexDrag < indexDrop) {
      thisColumnOver.classList.add('flip-list-move');
    } else {
      thisColumnOver.classList.add('flip-list-move-top');
    }
    previousColumnOver = thisColumnOver;
  }
}

function onDragEnd(event: DragEvent) {
  event.preventDefault();
  const thisColumnLeave = document.getElementById('drop-area' + keyDragHover.value);
  if (thisColumnLeave) {
    thisColumnLeave.classList.remove('flip-list-move');
    thisColumnLeave.classList.remove('flip-list-move-top');
    keyDragHover.value = null;
  }
}

function onDrop(event: DragEvent, key: string) {
  event.preventDefault();

  if (keyDragColumn.value != null) {
    const indexDrag = columns.value.findIndex((item) => item.key == keyDragColumn.value);
    const indexDrop = columns.value.findIndex((item) => item.key == key);
    columns.value.splice(indexDrag, 1);
    if (dataDragColumn.value != null) {
      columns.value.splice(indexDrop, 0, dataDragColumn.value);
    }
  }
  idColumnList.value++;
  keyDragColumn.value = null;
  dataDragColumn.value = null;
}

function onClickCancelButton() {
  vm?.$emit('close-modal');
}

function onClickApplyButton() {
  columns.value.push({ key: 'action', label: '', selected: true });
  vm?.$emit('apply-modal', columns.value);
  columns.value = [];
}

function onOpenModal() {
  columns.value = [];
  selectedColumns.value = [];
  const filteredColumns = props.columnValue.filter((column: any) => column.key != 'action');

  filteredColumns.forEach((column: any) => {
    columns.value.push({ key: column.key, label: column.label, selected: column.selected == null ? true : column.selected });
  });
  selectedColumns.value = columns.value.filter((column) => column.selected);
}
const onCloseModal = () => {
  vm?.$emit('close-modal');
};
watch(
  () => isSelectAllColumn.value,
  () => {
    // หาก isSelectAllColumn เป็น true ให้เลือกทุกคอลัมน์
    if (isSelectAllColumn.value) {
      columns.value.forEach((column) => {
        column.selected = true; // เปลี่ยนค่า selected เป็น true
      });
    } else {
      // หาก isSelectAllColumn เป็น false และมีคอลัมน์ที่ถูกเลือกอยู่
      // ให้เช็คว่าเป็น true ทุกตัวหรือไม่
      if (columns.value.every((column) => column.selected)) {
        columns.value.forEach((column) => {
          column.selected = false; // เปลี่ยนค่า selected เป็น false
        });
      }
    }
    // อัปเดต selectedColumns
    selectedColumns.value = columns.value.filter((column) => column.selected);
  },
  { deep: true },
);

watch(
  () => columns.value,
  () => {
    // เช็คว่าคอลัมน์ทุกตัวถูกเลือกหรือไม่
    isSelectAllColumn.value = columns.value.every((column) => column.selected);

    // อัปเดต selectedColumns
    selectedColumns.value = columns.value.filter((column) => column.selected);
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
.title-question-icon {
  padding-left: 0.5rem;
  width: 25px;
  height: auto;
  fill: #707070;
  &:hover {
    fill: #000000;
  }
}
.tooltip-question {
  position: relative;
  display: inline-block;

  &:hover {
    .tooltip-text {
      visibility: visible;
    }
  }

  .tooltip-text {
    position: relative;
    visibility: hidden;
    background-color: #efefef;
    text-align: center;
    border-radius: 6px;
    padding: 1rem;
    max-width: 370px;
    width: max-content;

    /* Position the tooltip */
    position: absolute;
    z-index: 1;
    top: -5px;
    left: 40px;
    font-weight: 400;
    text-align: left;
    .title {
      color: #000000;
    }
    .detail {
      color: #707070;
    }

    &::after {
      content: '';
      position: absolute;
      left: -9px;
      top: 10px;
      width: 0px;
      height: 0px;

      border-right: 10px solid #efefef;
      border-bottom: 5px solid transparent;
      border-top: 5px solid transparent;
    }
  }
}
.flip-list-move {
  transition: transform 0.5s;
  border-bottom: 3px solid #007fff; /* Example styling for the highlight effect */
}
.flip-list-move-top {
  transition: transform 0.5s;
  border-top: 3px solid #007fff; /* Example styling for the highlight effect */
}
:deep(.modal-inner) {
  max-height: 60rem !important;
  max-width: 60rem !important;
  width: 100% !important;
  overflow: auto !important;
}

:deep(.modal-body) {
  padding: 0 !important;
}
.manage-column-header-wrp {
  .manage-column-header {
    font-family: Prompt;
    font-size: 20px;
    font-weight: 500;
    line-height: 30.24px;
    text-align: left;
    color: #007fff;
  }
  .manage-column-header-description {
    font-family: Prompt;
    font-size: 16px;
    font-weight: 400;
    line-height: 30.24px;
    text-align: left;
    color: #4b4b4b;
  }
}
.manage-column-content-wrp {
  .content {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    .left-panel {
      margin-top: 1rem;
      flex: 1;
      .column-list {
        margin-top: 1rem;
        height: 33rem;
        overflow-y: auto;
        border-radius: 8px;
        padding: 10px;
        .column-item {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }
        .column-item label {
          margin-left: 15px;
        }
      }
      .table-header-column-container {
        display: flex;
        justify-content: flex-end;
        width: 100%;

        .table-header-column {
          display: flex;
          max-width: 130px;
          max-height: 45px;
          padding: 10px;
          border-radius: 10px;
          background: #fff;
          border: 1px solid #007fff;

          .icon-table-column {
            width: 20px;
            height: 20px;
            stroke: #007fff;
          }
          .text-table-column {
            margin-left: 0.5rem;
            font-family: Prompt;
            font-size: 18px;
            font-weight: 400;
            text-align: left;
            color: #007fff;
          }
        }
      }
    }
    .right-panel {
      flex: 1;
      background-color: #f5f5f5;
      .selected-column-header {
        font-family: Prompt;
        font-size: 18px;
        font-weight: 500;
        line-height: 30.24px;
        text-align: left;
        color: #2d2d2d;
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 3rem;
        width: 95%;
      }
      .selected-column-list {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 0.75rem;
        list-style: none;
        padding: 0;
        height: 33rem;
        width: 100%;
        overflow: auto;
      }
      .selected-column-list li {
        display: flex;
        width: 90%;
        background-color: #fff;
        border-radius: 5px;
        padding: 10px;
        margin-bottom: 5px;
        .text-selected-column-list {
          margin-left: 1rem;
        }
      }
    }
  }

  .actions {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-top: 2rem;
    margin-bottom: 1.5rem;

    .cancel-button {
      cursor: pointer;
      min-width: 6.25rem;
    }
    .apply-button {
      cursor: pointer;
      min-width: 6.25rem;
    }
  }
}
input {
  accent-color: #027fff;
}
</style>
